import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import type { InvoiceTransactionDraftDTO } from '@inversor-hub/types';
import useSnackbar from '@/hooks/useSnackbar';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';
import { useUploadInvoiceFile } from '@/lib/api/investment-transactions';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from './constants';
import { OptionHead } from './shared';

interface UploadInvoiceDialogProps {
  open: boolean;
  forwardingAddress?: string;
  onClose: () => void;
  onUploaded: (draft: InvoiceTransactionDraftDTO) => void;
}

export const UploadInvoiceDialog = ({
  open,
  forwardingAddress,
  onClose,
  onUploaded,
}: UploadInvoiceDialogProps) => {
  const { show } = useSnackbar();
  const { trackEvent } = useAnalytics();
  const uploadInvoice = useUploadInvoiceFile();
  const [processing, setProcessing] = useState(false);
  const [copied, setCopied] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        show({
          title: 'Algunos archivos no se pudieron subir',
          message: 'Solo se aceptan PDF, PNG o JPG de hasta 10 MB.',
          variant: 'error',
        });
      }
      if (acceptedFiles.length === 0) return;

      setProcessing(true);
      let firstDraft: InvoiceTransactionDraftDTO | null = null;
      let uploaded = 0;

      for (const file of acceptedFiles) {
        try {
          const draft = await uploadInvoice.mutateAsync({ file });
          uploaded += 1;
          if (!firstDraft) firstDraft = draft;
        } catch {
          show({ title: `No se pudo subir ${file.name}`, variant: 'error' });
        }
      }

      setProcessing(false);

      if (uploaded > 0) {
        trackProductEvent(trackEvent, 'invoice_uploaded', {
          file_count: uploaded,
          platform: ANALYTICS_PLATFORM,
        });
      }

      if (firstDraft) onUploaded(firstDraft);
    },
    [onUploaded, show, trackEvent, uploadInvoice]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    onDrop,
    noClick: false,
  });

  const handleCopy = async () => {
    if (!forwardingAddress) return;

    try {
      await navigator.clipboard.writeText(forwardingAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      trackProductEvent(trackEvent, 'invoice_forwarding_address_copied', {
        platform: ANALYTICS_PLATFORM,
      });
    } catch {
      show({ title: 'No se pudo copiar la dirección', variant: 'error' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { borderRadius: '20px', width: '100%', maxWidth: 760, m: 2 } }}
    >
      <Box sx={{ p: { xs: '24px 22px', sm: '28px 32px 30px' } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary' }}>
              Subir factura
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.6, maxWidth: 520 }}>
              La IA lee el documento y crea un{' '}
              <Box component="strong" sx={{ color: 'text.primary' }}>
                borrador de transacción
              </Box>
              . Tú lo revisas y confirmas antes de que se guarde.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={processing}
            aria-label="Cerrar"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {processing ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress sx={{ color: 'primary.main', mb: 2.25 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
              La IA está leyendo tu factura…
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.75 }}>
              Extrayendo emisor, importe, fecha y categoría.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: 2.75,
            }}
          >
            <Box
              sx={{
                border: 1.5,
                borderColor: 'grey.100',
                borderRadius: '16px',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <OptionHead
                n="1"
                icon={<UploadFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                title="Subir un archivo"
              />
              <Box
                {...getRootProps()}
                sx={{
                  flex: 1,
                  mt: 1.75,
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: isDragActive ? 'primary.main' : 'primary.light',
                  borderRadius: '12px',
                  bgcolor: isDragActive
                    ? 'primary.50'
                    : (theme) =>
                        alpha(theme.palette.primary[50] ?? theme.palette.primary.light, 0.5),
                  cursor: 'pointer',
                  p: '26px 18px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  transition: 'all .2s',
                }}
              >
                <input {...getInputProps()} aria-label="Subir factura" />
                <CloudUploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                  Arrastra el archivo aquí
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  o haz clic para seleccionarlo
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={openFileDialog}
                sx={{ mt: 1.75 }}
              >
                Seleccionar archivo
              </Button>
              <Typography sx={{ fontSize: 11.5, color: 'grey.300', mt: 1.25, textAlign: 'center' }}>
                PDF, JPG o PNG · hasta 10 MB
              </Typography>
            </Box>

            <Box
              sx={{
                border: 1.5,
                borderColor: 'grey.100',
                borderRadius: '16px',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <OptionHead
                n="2"
                icon={<MailOutlineIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
                title="Enviar por email"
              />
              <Typography
                sx={{ fontSize: 13.5, color: 'text.secondary', mt: 1.75, lineHeight: 1.55 }}
              >
                Reenvía tus facturas a tu dirección personal. Cada una llegará a la bandeja lista
                para revisar.
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  mt: 1.75,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'grey.100',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <AlternateEmailIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {forwardingAddress ?? 'Generando tu dirección…'}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCopy}
                  disabled={!forwardingAddress}
                  aria-label={copied ? 'Dirección copiada' : 'Copiar dirección de email'}
                  sx={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    width: 92,
                    height: 34,
                    bgcolor: copied ? 'success.50' : 'background.paper',
                    border: 1,
                    borderColor: copied ? 'success.main' : 'grey.200',
                    color: copied ? 'success.main' : 'primary.dark',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    fontSize: 12.5,
                    borderRadius: '8px',
                    cursor: forwardingAddress ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                    transition:
                      'background-color .18s ease, border-color .18s ease, color .18s ease, box-shadow .18s ease, transform .18s ease',
                    boxShadow: copied
                      ? (theme) => `0 0 0 3px ${alpha(theme.palette.success.main, 0.14)}`
                      : 'none',
                    animation: copied ? 'copySuccess .28s ease-out' : 'none',
                    '&:hover': {
                      bgcolor: copied ? 'success.50' : 'grey.50',
                      borderColor: copied ? 'success.main' : 'primary.light',
                    },
                    '&:active': {
                      transform: forwardingAddress ? 'scale(0.97)' : 'none',
                    },
                    '@keyframes copySuccess': {
                      '0%': { transform: 'scale(0.96)' },
                      '60%': { transform: 'scale(1.04)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.6}
                    aria-hidden={copied}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: copied ? 0 : 1,
                      transform: copied ? 'translateY(-8px)' : 'translateY(0)',
                      transition: 'opacity .16s ease, transform .16s ease',
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                    <Box component="span">Copiar</Box>
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.6}
                    aria-hidden={!copied}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: copied ? 1 : 0,
                      transform: copied ? 'translateY(0)' : 'translateY(8px)',
                      transition: 'opacity .16s ease, transform .16s ease',
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14 }} />
                    <Box component="span">Copiado</Box>
                  </Stack>
                </Box>
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.75 }}>
                <VerifiedUserOutlinedIcon sx={{ fontSize: 15, color: 'success.main' }} />
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  Solo tú recibes facturas en esta dirección.
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
